// Slide-over detail card for an FAA WeatherCams site.
//
// Shows the live camera grid (typically 4 cameras at NW/NE/SE/SW
// quadrants, but the FAA varies the count and orientation per site),
// a recent METAR if reported, and a 12-image history scrubber per
// camera so pilots can see how conditions are trending.
//
// Wiley Anderson feedback (2026-05-09): "The location and ability to
// click on weather cameras, and their location would be very helpful
// as well."

import { useState } from 'react';
import { formatLatLonDMM } from '../lib/coords';
import { useFetch } from '../hooks/useFetch';

interface CameraImage {
  url: string;
  capturedAt: string;
}

interface CameraDetail {
  id: number;
  name: string;
  direction: string;
  bearingDeg: number;
  lat: number;
  lon: number;
  active: boolean;
  lastSuccessAt: string;
  currentImage: CameraImage | null;
  history: CameraImage[];
}

interface SiteDetail {
  id: number;
  name: string;
  ident: string;
  icao: string;
  lat: number;
  lon: number;
  elevFt: number;
  country: string;
  cameras: CameraDetail[];
  metar: string | null;
  timeZone: string | null;
}

export interface WxCamSeed {
  camId: number;
  ident: string;
  name: string;
  lat: number;
  lon: number;
}

export default function WxCamDetail({ seed, onClose }: { seed: WxCamSeed; onClose: () => void }) {
  const { data: detail, loading: detailLoading, error: detailError } = useFetch(
    () => fetch(`/api/wx/cameras-site?id=${seed.camId}`).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<SiteDetail>;
    }),
    [seed.camId],
  );
  const error = detailError != null ? (detailError instanceof Error ? detailError.message : String(detailError)) : null;
  const [historyIdx, setHistoryIdx] = useState<Record<number, number>>({});

  const showImage = (cam: CameraDetail) => {
    const idx = historyIdx[cam.id] ?? 0;
    if (idx === 0 || !cam.history[idx]) return cam.currentImage;
    return cam.history[idx];
  };

  return (
    <div className="absolute right-3 top-3 z-30 w-[min(28rem,calc(100vw-1.5rem))] max-h-[calc(100vh-2rem)] bg-cockpit-900/95 border border-cockpit-700 rounded-xl shadow-2xl overflow-hidden flex flex-col backdrop-blur">
      {/* Header */}
      <div className="px-4 py-3 border-b border-cockpit-700 flex items-start justify-between gap-3 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-cockpit-100 text-base">{seed.ident || `WC${seed.camId}`}</span>
            {detail?.icao && detail.icao !== seed.ident && (
              <span className="font-mono text-[11px] text-cockpit-400">{detail.icao}</span>
            )}
          </div>
          <div className="text-xs text-cockpit-300 truncate">{seed.name}</div>
          <div className="text-[10px] text-cockpit-400 mt-0.5 font-mono">
            {formatLatLonDMM(seed.lat, seed.lon)}
            {detail?.elevFt != null && <> &middot; {detail.elevFt.toLocaleString()} ft MSL</>}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-cockpit-300 hover:text-cockpit-100 text-lg leading-none px-2 -my-1"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1 px-3 py-3 space-y-3">
        {error && (
          <div className="text-xs text-red-400 bg-red-950/40 border border-red-800 rounded-md px-3 py-2">
            Camera fetch failed: {error}
          </div>
        )}
        {detailLoading && (
          <div className="text-xs text-cockpit-400 italic px-1">Loading cameras…</div>
        )}

        {detail && (
          <>
            {/* METAR if reported */}
            {detail.metar && (
              <div>
                <div className="text-[10px] tracking-widest text-cockpit-400 mb-1">METAR</div>
                <div className="font-mono text-[11px] text-cockpit-100 bg-cockpit-800/60 border border-cockpit-700 rounded-md px-2 py-1.5 break-words">
                  {detail.metar}
                </div>
              </div>
            )}

            {/* Camera grid */}
            <div>
              <div className="text-[10px] tracking-widest text-cockpit-400 mb-1">
                {detail.cameras.length} CAMERA{detail.cameras.length === 1 ? '' : 'S'}
                {detail.timeZone && <span className="ml-2 normal-case text-cockpit-500">{detail.timeZone}</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {detail.cameras.map((cam) => {
                  const img = showImage(cam);
                  const idx = historyIdx[cam.id] ?? 0;
                  return (
                    <div key={cam.id} className="bg-cockpit-800/60 border border-cockpit-700 rounded-md overflow-hidden">
                      <div className="px-2 py-1 flex items-center justify-between gap-2 text-[11px]">
                        <span className="text-cockpit-100">
                          {cam.direction || cam.name}
                          {Number.isFinite(cam.bearingDeg) && (
                            <span className="text-cockpit-400 ml-1.5 font-mono">
                              {Math.round(cam.bearingDeg).toString().padStart(3, '0')}°
                            </span>
                          )}
                        </span>
                        {img && (
                          <span className="text-cockpit-400 font-mono text-[10px]">
                            {formatRelativeTime(img.capturedAt)}
                          </span>
                        )}
                      </div>
                      {img ? (
                        <a href={img.url} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] bg-black">
                          <img
                            src={img.url}
                            alt={`${cam.direction || cam.name} — ${formatRelativeTime(img.capturedAt)}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <div className="aspect-[4/3] bg-cockpit-800 flex items-center justify-center text-xs text-cockpit-400 italic">
                          {cam.active ? 'No recent image' : 'Out of service'}
                        </div>
                      )}
                      {cam.history.length > 1 && (
                        // History slider: right edge = now, left edge = oldest.
                        // Internal `idx` keeps 0 = most-recent semantics (so showImage()
                        // and the rest of the component don't change), but the slider's
                        // displayed/written value is inverted so dragging RIGHT moves
                        // forward in time. Boss request 2026-05-28: the prior orientation
                        // (right = oldest) felt backwards.
                        <div className="px-2 py-1.5 border-t border-cockpit-700 flex items-center gap-2">
                          <span className="text-[10px] text-cockpit-400 font-mono w-10 text-left">
                            {idx === 0 ? 'now' : `-${idx}`}
                          </span>
                          <input
                            type="range"
                            min={0}
                            max={cam.history.length - 1}
                            step={1}
                            value={(cam.history.length - 1) - idx}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              const inverted = (cam.history.length - 1) - v;
                              setHistoryIdx((s) => ({ ...s, [cam.id]: inverted }));
                            }}
                            aria-label="History (drag right for now, left for past)"
                            className="flex-1 accent-ember-500"
                          />
                          <span className="text-[10px] text-cockpit-400">Now</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="text-[10px] text-cockpit-500 italic px-1 pt-1">
              Source: FAA WeatherCams &middot; weathercams.faa.gov
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** "3 min ago" / "1 h ago" relative time. */
function formatRelativeTime(iso: string): string {
  const t = new Date(iso).valueOf();
  if (!Number.isFinite(t)) return '';
  const sec = Math.max(0, (Date.now() - t) / 1000);
  if (sec < 60) return `${Math.round(sec)}s ago`;
  const min = sec / 60;
  if (min < 60) return `${Math.round(min)}m ago`;
  const hr = min / 60;
  if (hr < 24) return `${hr.toFixed(1)}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}
